/**
 * Competition Management: listings (pivot doc §5 `listings.listing`, §6 Stage 3).
 *
 * A listing carries the prize and ticket details an operator enters, a copy
 * set an AI generator drafts from them, an approval per field group so a
 * draft never reaches Publish unreviewed, and a publish state per
 * destination with a log of what happened. The connector that owns the real
 * website publish does not exist yet: `website` here is a stub that records
 * intent only (pivot doc §6 Stage 3).
 */

import type { EntityRef } from '@/lib/drilldown'
import type { PacingStatus } from './types'

export const LISTING_STATUSES = ['draft', 'content_drafted', 'approved', 'published', 'needs_republish'] as const
export type ListingStatus = (typeof LISTING_STATUSES)[number]

export const LISTING_STATUS_LABELS: Record<ListingStatus, string> = {
  draft: 'Draft',
  content_drafted: 'Content drafted',
  approved: 'Approved',
  published: 'Published',
  /** Published somewhere, but a field group was edited or regenerated since and is no longer approved. */
  needs_republish: 'Needs republish',
}

/** The field groups a draft is reviewed and approved in, independently. */
export const FIELD_GROUPS = ['prizeDetails', 'copy'] as const
export type FieldGroupKey = (typeof FIELD_GROUPS)[number]

export const FIELD_GROUP_LABELS: Record<FieldGroupKey, string> = {
  prizeDetails: 'Prize & media details',
  copy: 'Generated copy',
}

export interface FieldGroupApproval {
  approved: boolean
  approvedAt: string | null
}

export type ListingApprovals = Record<FieldGroupKey, FieldGroupApproval>

/** The single publish-gating rule: every field group must be approved. Used by the store's status derivation, its publish guard, and the form's Publish buttons — one definition, so they cannot drift. */
export function allFieldGroupsApproved(approvals: ListingApprovals): boolean {
  return FIELD_GROUPS.every((group) => approvals[group].approved)
}

export const PUBLISH_TARGETS = ['website', 'pwa', 'email', 'push', 'social_ad_prep', 'venue_screens'] as const
export type PublishTargetKey = (typeof PUBLISH_TARGETS)[number]

export const PUBLISH_TARGET_LABELS: Record<PublishTargetKey, string> = {
  website: 'Website',
  pwa: 'PWA',
  email: 'Email',
  push: 'Push notifications',
  social_ad_prep: 'Social ad prep',
  venue_screens: 'Venue screens',
}

export type PublishStatus = 'not_published' | 'published'

export interface PublishTargetState {
  target: PublishTargetKey
  status: PublishStatus
  publishedAt: string | null
  /** True when this target was published but an approval has been reset since; a Republish is required. */
  stale: boolean
}

export interface PublishLogEntry {
  id: string
  listingId: string
  listingTitle: string
  target: PublishTargetKey
  status: PublishStatus
  message: string
  at: string
}

export interface ListingPrizeDetails {
  prizeName: string
  prizeCategory: string
  prizeValue: number
  ticketPrice: number
  maxEntries: number
  instantWinsCount: number
  /** ISO date the draw happens / the competition ends. */
  drawDate: string
  mainImageUrl: string
  additionalMediaUrls: string[]
  keySellingPoints: string[]
  prizeSpecifications: string
}

export interface ListingCopy {
  title: string
  shortDescription: string
  longDescription: string
  seoTitle: string
  seoDescription: string
  keyPoints: string[]
  emailCopy: string
  pushCopy: string
  socialCopy: string
  adCopy: string
  offlineFlyerHeadline: string
}

export interface ListingInput {
  prize: ListingPrizeDetails
  /** A tenant competition this listing pairs with, for its pacing link — or `null` for a standalone listing. */
  competitionId: string | null
}

export interface Listing {
  id: string
  status: ListingStatus
  prize: ListingPrizeDetails
  /** `null` until "Generate listing content" has run at least once. */
  copy: ListingCopy | null
  approvals: ListingApprovals
  publishing: PublishTargetState[]
  publishLog: PublishLogEntry[]
  competitionEntity: EntityRef | null
  pacing: PacingStatus | null
  sellThrough: number | null
  createdAt: string
  updatedAt: string
}

export type ListingTemplatePrize = Omit<ListingPrizeDetails, 'drawDate'>

export interface ListingTemplate {
  id: string
  name: string
  description: string
  prize: ListingTemplatePrize
}

export interface ListingTemplateInput {
  name: string
  description: string
  prize: ListingTemplatePrize
}

export function emptyListingCopy(): ListingCopy {
  return {
    title: '',
    shortDescription: '',
    longDescription: '',
    seoTitle: '',
    seoDescription: '',
    keyPoints: [],
    emailCopy: '',
    pushCopy: '',
    socialCopy: '',
    adCopy: '',
    offlineFlyerHeadline: '',
  }
}

/**
 * The AI content-drafting port (pivot doc §6 Stage 3, assumption 4): a pure,
 * deterministic stub today, swapped for a real provider behind the same
 * signature once one is chosen. No network calls belong behind it.
 */
export interface ListingContentGenerator {
  generate(prize: ListingPrizeDetails, tenantName: string): ListingCopy
}
